import { useI18n } from 'vue-i18n';
import { useUploadLimit } from '@/composables/useUploadLimit';

/**
 * The image handed to the guard by `ImageUploadButton`: the dimensions of the loaded
 * `HTMLImageElement`, plus the size of the file it was read from. `byteSize` is
 * optional because a caller that has only an element, and not the file behind it,
 * cannot supply it - the maximum-size check is then skipped rather than guessed at.
 */
export interface ValidatableImage {
  width: number;
  height: number;
  byteSize?: number;
}

export interface ImageValidationOutcome {
  hasError: boolean;
  hasWarning: boolean;
  errorMessage: string;
  warningMessage: string;
}

export function useImageValidation() {
  const { t } = useI18n();
  const { maxFileBytes } = useUploadLimit();

  /**
   * Checks an image against the constraints its uploader was configured with.
   *
   * Every failure is collected, so an image that breaks several rules is reported
   * once with all of them. They are whole sentences joined by a space rather than a
   * clause list, which keeps each message translatable on its own.
   *
   * Without a `maxImageKilobyteSize` of its own, an image may be as large as the
   * server's upload limit allows.
   */
  const validateImage = (
    image: ValidatableImage,
    squareImage: boolean,
    maxImageKilobyteSize: number | undefined,
    minImageDimSize: number,
    minWidthImageDimSize: number,
    minHeightImageDimSize: number,
  ): ImageValidationOutcome => {
    const errors: string[] = [];
    let warningMessage = '';

    if (squareImage && image.width !== image.height) {
      const big = Math.max(image.width, image.height);
      const small = Math.min(image.width, image.height);
      const percentageDivergence = 100 - ((small / big) * 100);
      if (percentageDivergence > 10) {
        warningMessage = t('images.validation.not_square');
      }
    }

    const maxKilobytes = maxImageKilobyteSize ?? Math.floor(maxFileBytes / 1024);
    if (maxKilobytes > 0 && image.byteSize !== undefined && image.byteSize > (maxKilobytes * 1024)) {
      errors.push(t('images.validation.too_large', {
        size: Math.ceil(image.byteSize / 1024),
        max: maxKilobytes,
      }));
    }

    if (minImageDimSize > 0 && (minImageDimSize > image.width || minImageDimSize > image.height)) {
      errors.push(t('images.validation.min_dimensions', { size: minImageDimSize }));
    }

    if (minWidthImageDimSize > 0 && (minWidthImageDimSize > image.width)) {
      errors.push(t('images.validation.min_width', { size: minWidthImageDimSize }));
    }

    if (minHeightImageDimSize > 0 && (minHeightImageDimSize > image.height)) {
      errors.push(t('images.validation.min_height', { size: minHeightImageDimSize }));
    }

    return {
      hasError: errors.length > 0,
      hasWarning: warningMessage !== '',
      errorMessage: errors.join(' '),
      warningMessage,
    };
  };

  return {
    validateImage,
  };
}
